// Language translations for Decoded platform
// Currently supports: English (en), Spanish (es)

export const translations = {
  en: {
    // Common
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    search: "Search",
    export: "Export",
    download: "Download",
    share: "Share",
    back: "Back",
    next: "Next",
    submit: "Submit",
    confirm: "Confirm",
    close: "Close",
    
    // Navigation
    nav: {
      dashboard: "Dashboard",
      productAnalyzer: "Product Analyzer",
      smartSourcing: "Smart Sourcing",
      originPreference: "Origin & Preference",
      financialAnalyser: "Financial Analyser",
      refundRadar: "Refund Radar",
      governance: "Governance",
      analysisHistory: "Analysis History",
      team: "Team",
      teamShared: "Team Shared",
      billingPlans: "Billing & Plans",
      settings: "Settings",
      adminPortal: "Admin Portal",
      signOut: "Sign Out"
    },
    
    // Auth
    auth: {
      login: "Log In",
      signup: "Sign Up",
      logout: "Log Out",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      fullName: "Full Name",
      forgotPassword: "Forgot Password?",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      orContinueWith: "Or continue with",
      loginSuccess: "Logged in successfully",
      logoutSuccess: "Logged out successfully",
      signupSuccess: "Account created successfully"
    },
    
    // Dashboard
    dashboard: {
      title: "Dashboard",
      welcome: "Welcome back",
      totalAnalyses: "Total Analyses",
      potentialSavings: "Potential Savings",
      riskAlerts: "Risk Alerts",
      recentActivity: "Recent Activity",
      quickActions: "Quick Actions",
      viewAll: "View All",
      noData: "No data yet",
      startAnalysis: "Start your first analysis"
    },
    
    // Product Analyzer
    analyzer: {
      title: "Product Analyzer",
      singleProduct: "Single Product",
      bulkUpload: "Bulk Upload",
      erpImport: "ERP Smart Import",
      productDescription: "Product Description",
      productDescriptionPlaceholder: "Describe your product in detail...",
      countryOfOrigin: "Country of Origin",
      importCountry: "Import Country",
      currentHsCode: "Current HS Code (Optional)",
      customsValue: "Customs Value (USD)",
      analyze: "Analyze Product",
      analyzing: "Analyzing...",
      results: "Results",
      suggestedHsCode: "Suggested HS Code",
      confidence: "Confidence",
      potentialSavings: "Potential Savings",
      currentTariff: "Current Tariff",
      suggestedTariff: "Suggested Tariff",
      alternatives: "Alternative Classifications",
      reasoning: "AI Reasoning",
      uploadFile: "Upload File",
      dragDrop: "Drag and drop your CSV or Excel file here",
      browse: "Browse",
      processing: "Processing...",
      processed: "processed"
    },
    
    // Financial Impact
    financial: {
      title: "Financial Impact Analyzer",
      uploadTransactions: "Upload Transaction Data",
      analyzing: "Analyzing transactions...",
      totalExposure: "Total Exposure",
      overpayment: "Overpayment",
      underpaymentRisk: "Underpayment Risk",
      skusAnalyzed: "SKUs Analyzed",
      transactionsProcessed: "Transactions Processed",
      topFindings: "Top Findings",
      filterByValue: "Filter by minimum value",
      priorityOnly: "Analyze top 20% only (80/20 rule)"
    },
    
    // Billing
    billing: {
      title: "Billing & Plans",
      currentPlan: "Current Plan",
      upgrade: "Upgrade",
      downgrade: "Downgrade",
      contactSales: "Contact Sales",
      usageThisMonth: "Usage This Month",
      creditsUsed: "Credits Used",
      resetsOn: "Resets on",
      features: "Features",
      perMonth: "/month",
      unlimited: "Unlimited",
      free: "Free",
      starter: "Starter",
      growth: "Growth",
      enterprise: "Enterprise",
      currentTier: "Current"
    },
    
    // Settings
    settings: {
      title: "Settings",
      subtitle: "Manage your account and preferences",
      aiModelPreference: "AI Model Preference",
      chooseModel: "Choose your default AI model for analysis",
      creditsPerAnalysis: "credits/analysis",
      howCreditsWork: "How credits work:",
      creditsExplanation: "Each analysis consumes credits based on the model you choose. Gemini Flash is the most cost-effective (1 credit), while premium models like GPT-4o (30 credits) and Claude Sonnet (46 credits) offer enhanced capabilities at higher credit costs.",
      profileInfo: "Profile Information",
      accountDetails: "Account Details",
      subscription: "Subscription",
      skuLimit: "SKU Limit",
      totalAnalyses: "Total Analyses",
      language: "Language",
      languageSubtitle: "Choose your preferred language",
      saveChanges: "Save Changes",
      emailCannotChange: "Email cannot be changed"
    },
    
    // Results & Reports
    results: {
      title: "Analysis Results",
      summary: "Summary",
      details: "Details",
      downloadPdf: "Download PDF",
      shareReport: "Share Report",
      copyLink: "Copy Link",
      linkCopied: "Link copied to clipboard",
      expiresIn: "Expires in",
      days: "days",
      classification: "Classification",
      landedCost: "Landed Cost",
      sourcing: "Sourcing Options"
    },
    
    // History
    history: {
      title: "Analysis History",
      noHistory: "No analyses yet",
      startFirst: "Run your first analysis to see it here",
      date: "Date",
      type: "Type",
      products: "Products",
      status: "Status",
      actions: "Actions",
      view: "View",
      single: "Single",
      bulk: "Bulk"
    },
    
    // Admin
    admin: {
      title: "Admin Portal",
      subtitle: "Manage users, subscriptions, and platform settings",
      overview: "Overview",
      users: "Users",
      promoCodes: "Promo Codes",
      totalUsers: "Total Users",
      monthlyRevenue: "Monthly Revenue",
      totalRevenue: "Total Revenue",
      totalAnalyses: "Total Analyses",
      usersByTier: "Users by Tier",
      searchUsers: "Search by email or name...",
      grantCredits: "Grant bonus credits:",
      createPromoCode: "Create Promo Code",
      code: "Code",
      discount: "Discount",
      uses: "Uses",
      status: "Status",
      createdBy: "Created By",
      active: "Active",
      inactive: "Inactive",
      deactivate: "Deactivate"
    },
    
    // Errors & Messages
    messages: {
      error: "Error",
      success: "Success",
      connectionError: "Connection error. Please try again.",
      sessionExpired: "Session expired. Please log in again.",
      limitReached: "Usage limit reached. Please upgrade your plan.",
      invalidFile: "Invalid file format",
      uploadSuccess: "File uploaded successfully",
      analysisComplete: "Analysis complete",
      savingChanges: "Saving changes...",
      changesSaved: "Changes saved successfully"
    },
    
    // Landing Page
    landing: {
      hero: {
        title: "AI-Powered Tariff Classification",
        subtitle: "Discover hidden duty savings in your product catalog with intelligent HS code optimization",
        cta: "Start Free Analysis",
        secondaryCta: "Watch Demo"
      },
      features: {
        title: "Everything you need for tariff optimization",
        classification: "Smart Classification",
        classificationDesc: "AI suggests optimal HS codes with confidence scoring",
        savings: "Savings Discovery",
        savingsDesc: "Identify duty overpayments and optimization opportunities",
        compliance: "Compliance Ready",
        complianceDesc: "Audit trails and documentation for customs compliance"
      },
      pricing: {
        title: "Simple, transparent pricing",
        subtitle: "Start free, scale as you grow"
      }
    }
  },
  
  es: {
    // Common
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    search: "Buscar",
    export: "Exportar",
    download: "Descargar",
    share: "Compartir",
    back: "Volver",
    next: "Siguiente",
    submit: "Enviar",
    confirm: "Confirmar",
    close: "Cerrar",
    
    // Navigation
    nav: {
      dashboard: "Panel de Control",
      productAnalyzer: "Analizador de Productos",
      smartSourcing: "Abastecimiento Inteligente",
      originPreference: "Origen y Preferencia",
      financialAnalyser: "Analizador Financiero",
      refundRadar: "Radar de Reembolsos",
      governance: "Gobernanza",
      analysisHistory: "Historial de Análisis",
      team: "Equipo",
      teamShared: "Compartido en Equipo",
      billingPlans: "Facturación y Planes",
      settings: "Configuración",
      adminPortal: "Portal de Administración",
      signOut: "Cerrar Sesión"
    },
    
    // Auth
    auth: {
      login: "Iniciar Sesión",
      signup: "Registrarse",
      logout: "Cerrar Sesión",
      email: "Correo Electrónico",
      password: "Contraseña",
      confirmPassword: "Confirmar Contraseña",
      fullName: "Nombre Completo",
      forgotPassword: "¿Olvidaste tu contraseña?",
      noAccount: "¿No tienes una cuenta?",
      hasAccount: "¿Ya tienes una cuenta?",
      orContinueWith: "O continuar con",
      loginSuccess: "Sesión iniciada correctamente",
      logoutSuccess: "Sesión cerrada correctamente",
      signupSuccess: "Cuenta creada correctamente"
    },
    
    // Dashboard
    dashboard: {
      title: "Panel de Control",
      welcome: "Bienvenido de nuevo",
      totalAnalyses: "Análisis Totales",
      potentialSavings: "Ahorros Potenciales",
      riskAlerts: "Alertas de Riesgo",
      recentActivity: "Actividad Reciente",
      quickActions: "Acciones Rápidas",
      viewAll: "Ver Todo",
      noData: "Sin datos aún",
      startAnalysis: "Comienza tu primer análisis"
    },
    
    // Product Analyzer
    analyzer: {
      title: "Analizador de Productos",
      singleProduct: "Producto Individual",
      bulkUpload: "Carga Masiva",
      erpImport: "Importación ERP Inteligente",
      productDescription: "Descripción del Producto",
      productDescriptionPlaceholder: "Describe tu producto en detalle...",
      countryOfOrigin: "País de Origen",
      importCountry: "País de Importación",
      currentHsCode: "Código HS Actual (Opcional)",
      customsValue: "Valor Aduanero (USD)",
      analyze: "Analizar Producto",
      analyzing: "Analizando...",
      results: "Resultados",
      suggestedHsCode: "Código HS Sugerido",
      confidence: "Confianza",
      potentialSavings: "Ahorros Potenciales",
      currentTariff: "Tarifa Actual",
      suggestedTariff: "Tarifa Sugerida",
      alternatives: "Clasificaciones Alternativas",
      reasoning: "Razonamiento de IA",
      uploadFile: "Subir Archivo",
      dragDrop: "Arrastra y suelta tu archivo CSV o Excel aquí",
      browse: "Explorar",
      processing: "Procesando...",
      processed: "procesados"
    },
    
    // Financial Impact
    financial: {
      title: "Analizador de Impacto Financiero",
      uploadTransactions: "Subir Datos de Transacciones",
      analyzing: "Analizando transacciones...",
      totalExposure: "Exposición Total",
      overpayment: "Sobrepago",
      underpaymentRisk: "Riesgo de Pago Insuficiente",
      skusAnalyzed: "SKUs Analizados",
      transactionsProcessed: "Transacciones Procesadas",
      topFindings: "Principales Hallazgos",
      filterByValue: "Filtrar por valor mínimo",
      priorityOnly: "Analizar solo el 20% superior (regla 80/20)"
    },
    
    // Billing
    billing: {
      title: "Facturación y Planes",
      currentPlan: "Plan Actual",
      upgrade: "Mejorar",
      downgrade: "Reducir",
      contactSales: "Contactar Ventas",
      usageThisMonth: "Uso Este Mes",
      creditsUsed: "Créditos Usados",
      resetsOn: "Se reinicia el",
      features: "Características",
      perMonth: "/mes",
      unlimited: "Ilimitado",
      free: "Gratis",
      starter: "Inicial",
      growth: "Crecimiento",
      enterprise: "Empresarial",
      currentTier: "Actual"
    },
    
    // Settings
    settings: {
      title: "Configuración",
      subtitle: "Administra tu cuenta y preferencias",
      aiModelPreference: "Preferencia de Modelo de IA",
      chooseModel: "Elige tu modelo de IA predeterminado para análisis",
      creditsPerAnalysis: "créditos/análisis",
      howCreditsWork: "Cómo funcionan los créditos:",
      creditsExplanation: "Cada análisis consume créditos según el modelo que elijas. Gemini Flash es el más económico (1 crédito), mientras que modelos premium como GPT-4o (30 créditos) y Claude Sonnet (46 créditos) ofrecen capacidades mejoradas a mayor costo de créditos.",
      profileInfo: "Información del Perfil",
      accountDetails: "Detalles de la Cuenta",
      subscription: "Suscripción",
      skuLimit: "Límite de SKU",
      totalAnalyses: "Análisis Totales",
      language: "Idioma",
      languageSubtitle: "Elige tu idioma preferido",
      saveChanges: "Guardar Cambios",
      emailCannotChange: "El correo no se puede cambiar"
    },
    
    // Results & Reports
    results: {
      title: "Resultados del Análisis",
      summary: "Resumen",
      details: "Detalles",
      downloadPdf: "Descargar PDF",
      shareReport: "Compartir Informe",
      copyLink: "Copiar Enlace",
      linkCopied: "Enlace copiado al portapapeles",
      expiresIn: "Expira en",
      days: "días",
      classification: "Clasificación",
      landedCost: "Costo de Desembarque",
      sourcing: "Opciones de Abastecimiento"
    },
    
    // History
    history: {
      title: "Historial de Análisis",
      noHistory: "Sin análisis aún",
      startFirst: "Ejecuta tu primer análisis para verlo aquí",
      date: "Fecha",
      type: "Tipo",
      products: "Productos",
      status: "Estado",
      actions: "Acciones",
      view: "Ver",
      single: "Individual",
      bulk: "Masivo"
    },
    
    // Admin
    admin: {
      title: "Portal de Administración",
      subtitle: "Administra usuarios, suscripciones y configuración de la plataforma",
      overview: "Resumen",
      users: "Usuarios",
      promoCodes: "Códigos Promocionales",
      totalUsers: "Usuarios Totales",
      monthlyRevenue: "Ingresos Mensuales",
      totalRevenue: "Ingresos Totales",
      totalAnalyses: "Análisis Totales",
      usersByTier: "Usuarios por Nivel",
      searchUsers: "Buscar por correo o nombre...",
      grantCredits: "Otorgar créditos adicionales:",
      createPromoCode: "Crear Código Promocional",
      code: "Código",
      discount: "Descuento",
      uses: "Usos",
      status: "Estado",
      createdBy: "Creado Por",
      active: "Activo",
      inactive: "Inactivo",
      deactivate: "Desactivar"
    },
    
    // Errors & Messages
    messages: {
      error: "Error",
      success: "Éxito",
      connectionError: "Error de conexión. Por favor, intenta de nuevo.",
      sessionExpired: "Sesión expirada. Por favor, inicia sesión nuevamente.",
      limitReached: "Límite de uso alcanzado. Por favor, mejora tu plan.",
      invalidFile: "Formato de archivo inválido",
      uploadSuccess: "Archivo subido correctamente",
      analysisComplete: "Análisis completado",
      savingChanges: "Guardando cambios...",
      changesSaved: "Cambios guardados correctamente"
    },
    
    // Landing Page
    landing: {
      hero: {
        title: "Clasificación Arancelaria con IA",
        subtitle: "Descubre ahorros ocultos en aranceles en tu catálogo de productos con optimización inteligente de códigos HS",
        cta: "Comenzar Análisis Gratis",
        secondaryCta: "Ver Demo"
      },
      features: {
        title: "Todo lo que necesitas para optimización arancelaria",
        classification: "Clasificación Inteligente",
        classificationDesc: "La IA sugiere códigos HS óptimos con puntuación de confianza",
        savings: "Descubrimiento de Ahorros",
        savingsDesc: "Identifica sobrepagos de aranceles y oportunidades de optimización",
        compliance: "Listo para Cumplimiento",
        complianceDesc: "Registros de auditoría y documentación para cumplimiento aduanero"
      },
      pricing: {
        title: "Precios simples y transparentes",
        subtitle: "Comienza gratis, escala a medida que creces"
      }
    }
  }
};

export const getTranslation = (lang, key) => {
  const keys = key.split('.');
  let value = translations[lang] || translations['en'];
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback to English
      value = translations['en'];
      for (const k2 of keys) {
        if (value && typeof value === 'object' && k2 in value) {
          value = value[k2];
        } else {
          return key; // Return key if translation not found
        }
      }
      break;
    }
  }
  
  return value || key;
};

export const availableLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' }
];

export default translations;
